import licenseComplianceService from '../services/licenseComplianceService';
import { getDeliveryMethodLabel } from './deliveryMethods';
import type {
  CreditLimitFulfillmentDto,
  LicenseFulfillmentDto,
  MultiLicenseFulfillmentDto,
} from '../services/licenseComplianceService';

// Logic for the course-detail credit-limit hint (CourseCreditLimitHint.tsx).
// Kept out of the component file so the component only exports a component
// (react-refresh) and the matching logic is unit-testable.

/** Show the hint once usage reaches this fraction of a matching cap. */
export const HINT_THRESHOLD = 0.8;

// Module-level memo so the three course-detail pages (and remounts while
// browsing the catalog) don't refetch fulfillment for every course viewed.
// Short TTL keeps the hint reasonably fresh after credits change. Keyed by the
// signed-in user's id: module state outlives sign-out/sign-in (and Clerk
// account switching never reloads the page), so a time-only cache could serve
// one user's compliance data to the next.
const FULFILLMENT_CACHE_TTL_MS = 60_000;
let cachedFulfillment: {
  userId: string;
  promise: Promise<MultiLicenseFulfillmentDto>;
  at: number;
} | null = null;

export function getFulfillmentMemo(userId: string): Promise<MultiLicenseFulfillmentDto> {
  const now = Date.now();
  if (
    !cachedFulfillment ||
    cachedFulfillment.userId !== userId ||
    now - cachedFulfillment.at > FULFILLMENT_CACHE_TTL_MS
  ) {
    const promise = licenseComplianceService.getFulfillment();
    cachedFulfillment = { userId, promise, at: now };
    // Don't cache failures — the next mount should retry.
    promise.catch(() => {
      if (cachedFulfillment?.promise === promise) cachedFulfillment = null;
    });
  }
  return cachedFulfillment.promise;
}

/** Test hook: clears the module-level fulfillment cache. */
export function clearFulfillmentCacheForTests(): void {
  cachedFulfillment = null;
}

export interface MatchedLimit {
  license: LicenseFulfillmentDto;
  limit: CreditLimitFulfillmentDto;
  /** Usage fraction of the enforceable cap (current-year bucket for per-year limits). */
  ratio: number;
  used: number;
  cap: number;
  /**
   * What the course matched on: the delivery-method label for delivery-basis
   * limits, or the intersecting field-of-study names for field-basis limits.
   */
  label: string;
}

/** The course attributes a credit limit can match against. */
export interface CourseCreditProfile {
  /** Classified delivery method token; null = unclassifiable (exempt from delivery caps). */
  deliveryMethod: string | null;
  /** Education category ids the course awards credit in. */
  educationCategoryIds: number[];
}

/**
 * For per-year limits, judge against the bucket containing "now" so a cap
 * already exhausted in a past year doesn't warn about the current one.
 */
function currentUsage(
  limit: CreditLimitFulfillmentDto,
  now: Date
): { used: number; cap: number } | null {
  const buckets = limit.yearBuckets;
  if (buckets && buckets.length > 0) {
    // Fall back to the nearest bucket when "now" is outside every bucket:
    // first bucket for a not-yet-started period, last for an elapsed one.
    const bucket =
      buckets.find((b) => new Date(b.start) <= now && now <= new Date(b.end)) ??
      (now < new Date(buckets[0].start) ? buckets[0] : buckets[buckets.length - 1]);
    if (bucket.effectiveCapHours == null || bucket.effectiveCapHours <= 0) return null;
    return { used: bucket.usedHours, cap: bucket.effectiveCapHours };
  }
  if (limit.effectiveCapHours == null || limit.effectiveCapHours <= 0) return null;
  return { used: limit.usedHours, cap: limit.effectiveCapHours };
}

/**
 * How a limit matches the course, by the limit's own basis: field-basis limits
 * match when the course awards credit in a capped category; delivery-basis
 * limits match the classified delivery method. Returns the matched label, or
 * null when the limit doesn't apply.
 */
function matchLabel(limit: CreditLimitFulfillmentDto, course: CourseCreditProfile): string | null {
  if (limit.educationCategoryIds.length > 0) {
    const names = limit.educationCategoryIds
      .map((id, i) => (course.educationCategoryIds.includes(id) ? limit.educationCategoryNames[i] : null))
      .filter((n): n is string => n != null);
    return names.length > 0 ? names.join(', ') : null;
  }
  if (course.deliveryMethod && limit.deliveryMethods.includes(course.deliveryMethod)) {
    return getDeliveryMethodLabel(course.deliveryMethod);
  }
  return null;
}

/** Picks the most-constrained (highest used/cap ratio) matching limit across licenses. */
export function findMostConstrainedLimit(
  licenses: LicenseFulfillmentDto[],
  course: CourseCreditProfile,
  now: Date = new Date()
): MatchedLimit | null {
  let best: MatchedLimit | null = null;
  for (const license of licenses) {
    for (const limit of license.creditLimits ?? []) {
      const label = matchLabel(limit, course);
      if (label == null) continue;
      const usage = currentUsage(limit, now);
      if (!usage) continue;
      const ratio = usage.used / usage.cap;
      if (!best || ratio > best.ratio) {
        best = { license, limit, ratio, used: usage.used, cap: usage.cap, label };
      }
    }
  }
  return best;
}
